import React, { useCallback, useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import NotFound from "@/pages/NotFound";
import AccessDenied from "@/pages/AccessDenied";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { usePhaseFeature } from "../../lib/phaseFeatures";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";

export function SharingGate({ children }) {
  const { user } = useAuth();
  const f = usePhaseFeature(6, user?.id);
  if (!f) return <div className="p-8 text-sm font-nav text-erf-dark/70" data-testid="phase6-gate-loading">Loading…</div>;
  if (!["OWNER_SUPER_ADMIN", "ADMINISTRATOR"].includes(user?.role_key)) return <AccessDenied />; // B2a: role check first → 403 page (no module-state leak)
  if (!f.enabled) return <NotFound />;
  return children;
}

function ProjectSharing({ pid }) {
  const [clients, setClients] = useState([]);
  const [shared, setShared] = useState([]);
  const [email, setEmail] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    api.get(`/phase6/projects/${pid}/clients`).then((r) => setClients(r.data));
    api.get(`/phase6/projects/${pid}/shared`).then((r) => setShared(r.data));
    api.get(`/phase6/projects/${pid}/shareable`).then((r) => setCandidates(r.data));
  }, [pid]);
  useEffect(() => { load(); }, [load]);
  const run = (p) => p.then(() => { setError(null); load(); }).catch((e) => setError(errMsg(e, "That did not work.")));
  return (
    <Card className="ds-card border-erf-borderCream" data-testid="phase6-sharing-panel">
      <CardContent className="p-5 space-y-4 text-sm font-nav text-erf-dark">
        <h2 className="font-display font-semibold text-base md:text-lg">Clients with access</h2>
        <ul className="space-y-2">
          {clients.length === 0 && <li>No clients yet.</li>}
          {clients.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2" data-testid={`phase6-client-${c.client_email}`}>
              <span className="mr-auto">{c.client_email}</span>
              <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.delete(`/phase6/grants/${c.id}`))} data-testid={`phase6-revoke-${c.client_email}`}>Remove access</Button>
            </li>
          ))}
        </ul>
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); run(api.post(`/phase6/projects/${pid}/clients`, { email }).then(() => setEmail(""))); }}>
          <label>Client email<Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11" data-testid="phase6-grant-email" /></label>
          <Button type="submit" className="h-11 bg-erf-dark text-[#FEFCF7]" data-testid="phase6-grant-btn"><UserPlus className="w-4 h-4 mr-1.5" aria-hidden="true" />Give access</Button>
        </form>
        <p className="text-xs text-erf-dark/80">The client must already have a Client account. Invite them from Users first.</p>
        <h2 className="font-display font-semibold text-base md:text-lg">Shared with clients</h2>
        <ul className="space-y-2">
          {shared.length === 0 && <li>Nothing is shared. Clients only see “Project onboarding”.</li>}
          {shared.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-2" data-testid={`phase6-shared-${s.id}`}>
              <span className="mr-auto">{s.title}</span>
              <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.delete(`/phase6/shared/${s.id}`))} data-testid={`phase6-unshare-${s.id}`}>Stop sharing</Button>
            </li>
          ))}
        </ul>
        <h2 className="font-display font-semibold text-base md:text-lg">Ready to share</h2>
        <p className="text-xs text-erf-dark/80">Only issued pay applications, approved drawings (status A–D), approved change orders, punch items and acknowledged daily-report summaries appear here. Clients never see full daily reports.</p>
        <ul className="space-y-2">
          {candidates.length === 0 && <li>Nothing ready to share.</li>}
          {candidates.map((c) => (
            <li key={c.item_id} className="flex flex-wrap items-center gap-2" data-testid={`phase6-candidate-${c.item_id}`}>
              <span className="mr-auto">{c.title}</span>
              <Button className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" onClick={() => run(api.post(`/phase6/projects/${pid}/shared`, { kind: c.kind, item_id: c.item_id }))} data-testid={`phase6-share-${c.item_id}`}>Share</Button>
            </li>
          ))}
        </ul>
        {error && <p role="alert" className="text-sm text-[#991B1B]" data-testid="phase6-sharing-error">{error}</p>}
      </CardContent>
    </Card>
  );
}

export default function ClientSharing() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const [pid, setPid] = useState(null);
  useEffect(() => { api.get("/phase6/projects").then((r) => { setProjects(r.data); setPid(r.data.items[0]?.id || null); }); }, []);
  return (
    <AdminAppShell activeNav="clientsharing" user={user} title="Client Sharing" subtitle="Choose which clients see a project, and exactly what they see."
      breadcrumbs={[{ label: "Owner Command Center", to: "/owner" }, { label: "Client Sharing", to: null }]}>
      <div data-testid="phase6-workspace">
        {projects && projects.items.length === 0 && <p className="text-sm font-nav text-erf-dark" data-testid="phase6-no-projects">No projects yet.</p>}
        <div className="flex flex-wrap gap-2 mb-5" role="group" aria-label="Choose a project" data-testid="phase6-project-picker">
          {(projects?.items || []).map((p) => (
            <Button key={p.id} aria-pressed={pid === p.id} onClick={() => setPid(p.id)} data-testid={`phase6-project-${p.id}`}
              variant={pid === p.id ? "default" : "outline"} className={pid === p.id ? "h-11 bg-erf-dark text-[#FEFCF7]" : "h-11 border-erf-borderCream text-erf-dark bg-white"}>{p.name}</Button>
          ))}
        </div>
        {pid && <ProjectSharing key={pid} pid={pid} />}
      </div>
    </AdminAppShell>
  );
}
